// src/shared/ui/Tabs.jsx
//
// Generalizes the `activeTab`/`setActiveTab` + hand-written tab-strip markup
// reimplemented independently in adai (AdaiPage/HistoryTab), rds_ui (SOWDetail,
// DesignStudio, GenerationConfig...), golive (ScriptViewer), and compliance
// (CompliancePage) — this renders just the tab strip; each caller keeps
// owning what content it shows for the active tab.
//
// Underline visual style: a full-width grey bar (--jade-neutral-100), each
// tab flush against it with a 2px bottom border — transparent normally,
// near-black (--color-ink) plus bold text when active. This is compliance's
// own idp-nav/idp-nav-tab CSS (confirmed correct against the actual
// original design) ported in as the shared default, replacing an earlier
// solid-pill style, so every consumer renders the identical bar instead of
// each module keeping its own. No horizontal padding of its own — a page
// should supply its own margin (matching compliance's idp-page, which
// relies on .orion-content-inner) rather than this component double-adding
// one; pass a className for spacing around the bar itself (e.g. `mb-5`), not
// padding inside it.
//
// Usage:
//   const [tab, setTab] = useState("overview");
//   <Tabs tabs={[{ key: "overview", label: "Overview" }, { key: "history", label: "History", icon: History }]}
//         active={tab} onChange={setTab} />
//   {tab === "overview" && <OverviewPanel />}

import PropTypes from "prop-types";

export default function Tabs({ tabs, active, onChange, className = "" }) {
  return (
    <div
      role="tablist"
      className={`flex items-center shrink-0 border-b border-line bg-(--jade-neutral-100) shadow-(--jade-sh-1) ${className}`.trim()}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        const Icon = tab.icon;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={tab.disabled}
            title={tab.title}
            onClick={() => !tab.disabled && onChange(tab.key)}
            className={[
              // rounded-none overrides index.css's global `button { border-radius: 8px }`
              // base reset — without it every tab's 2px bottom border inherits that
              // radius and renders as a curved/bracket shape instead of a flat line.
              "inline-flex items-center gap-1.5 px-4 py-3.5 -mb-px text-sm font-medium border-b-2 rounded-none transition-colors whitespace-nowrap",
              isActive
                ? "text-ink font-semibold border-ink"
                : "text-muted border-transparent hover:text-ink",
              tab.disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
            ].join(" ")}
          >
            {Icon && <Icon size={15} />}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.elementType,
      disabled: PropTypes.bool,
      title: PropTypes.string,
    })
  ).isRequired,
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};
